import { z } from 'zod';

/**
 * Create-wizard validation.
 *
 * Each step validates independently so "Weiter" can be blocked precisely, and the whole form is
 * revalidated at the end — a wizard that only checks the visible step is how invalid records get
 * created by clicking Back.
 *
 * What the operator supplies is exactly `CreateGroupPolicyRequest`: a policyholder, the group
 * tarif the agreement is created under, the tarifs its members may be enrolled in, the billing
 * terms and the day it takes effect. No version is named anywhere — the service freezes the group
 * tarif's latest active version itself (architecture §A.5), which is what keeps this screen free
 * of configuration vocabulary (SSOT I-6).
 */
const civilDate = z.iso.date();

export const partnerStepSchema = z.object({
  /**
   * The agreement's own business identity. OPTIONAL, and the contract says so: insurers arrive
   * with numbers already printed on documents, and the service mints one when this is empty.
   */
  code: z.string().trim(),
  policyholderPartnerId: z.string().trim().min(1, 'groupPolicies.wizard.partner.required'),
  effectiveFrom: civilDate,
});

export const productsStepSchema = z.object({
  groupTarifErn: z.string().min(1, 'groupPolicies.wizard.products.groupTarifRequired'),
  /** At least one tarif, because this list is the gate for creating any member (SSOT I-1). */
  allowedTarifs: z.array(z.string()).min(1, 'groupPolicies.wizard.products.required'),
  /**
   * The OTHER roles the chosen group tarif allows (SDD-011 P-8), per role the partner ids typed.
   * The policyholder is the partner step's field — it is required by every version (P-6) and
   * therefore known before a group tarif is chosen; the rest depends on the choice.
   */
  partners: z.record(z.string(), z.array(z.string())).optional(),
});

export const billingStepSchema = z.object({
  billing: z.object({
    frequency: z.enum(['MONTHLY', 'QUARTERLY', 'ANNUAL']),
    mode: z.enum(['AGGREGATE_CHILD_PREMIUMS', 'DIRECT']),
    /** A literal, not a choice: dunning exists only at master level (SSOT I-20). */
    dunning: z.literal('MASTER_ONLY'),
  }),
});

export const createGroupPolicySchema = partnerStepSchema
  .extend(productsStepSchema.shape)
  .extend(billingStepSchema.shape);

export type CreateGroupPolicyForm = z.infer<typeof createGroupPolicySchema>;

export const WIZARD_STEPS = ['partner', 'products', 'billing', 'summary'] as const;
export type WizardStep = (typeof WIZARD_STEPS)[number];

/** Which fields each step owns — drives per-step validation before advancing. */
export const STEP_FIELDS: Readonly<Record<WizardStep, readonly (keyof CreateGroupPolicyForm)[]>> = {
  partner: ['code', 'policyholderPartnerId', 'effectiveFrom'],
  products: ['groupTarifErn', 'allowedTarifs', 'partners'],
  billing: ['billing'],
  summary: [],
};

export const defaultCreateGroupPolicyForm: CreateGroupPolicyForm = {
  code: '',
  policyholderPartnerId: '',
  effectiveFrom: '',
  groupTarifErn: '',
  allowedTarifs: [],
  partners: {},
  billing: { frequency: 'MONTHLY', mode: 'AGGREGATE_CHILD_PREMIUMS', dunning: 'MASTER_ONLY' },
};
